import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, VpnKey as PasswordIcon, Security as SecurityIcon } from '@mui/icons-material';
import axios from 'axios';
import { universalModalPaperProps, universalModalBackdropProps } from '../styles/modalStyles';
import { ROLE_PERMISSIONS, Permission } from '../contexts/AuthContext';
import PermissionsManager from './PermissionsManager';

interface SalesforceUser {
  Id: string;
  Email__c: string;
  First_Name__c: string;
  Last_Name__c: string;
  Role__c: string;
  Is_Active__c: boolean;
  Last_Login__c: string | null;
  Permissions__c?: string; // JSON string of custom permissions
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<SalesforceUser[]>([]);
  const [open, setOpen] = useState(false);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<SalesforceUser | null>(null);
  const [password, setPassword] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    role: 'user',
    is_active: true,
  });

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedUserForPermissions, setSelectedUserForPermissions] = useState<SalesforceUser | null>(null);

  // Available roles and their descriptions
  const availableRoles = [
    { value: 'admin', label: 'Administrator', description: 'Full access to all features' },
    { value: 'user', label: 'User', description: 'Can access temporary certification cards' },
  ];

  // Helper function to get role permissions for display
  const getRolePermissions = (role: string) => {
    return ROLE_PERMISSIONS[role] || [];
  };

  // Helper function to get user's actual permissions (custom or role-based)
  const getUserPermissions = (user: SalesforceUser): Permission[] => {
    if (user.Permissions__c) {
      try {
        return JSON.parse(user.Permissions__c);
      } catch {
        return getRolePermissions(user.Role__c);
      }
    }
    return getRolePermissions(user.Role__c);
  };

  const handlePermissionsDialogOpen = (user: SalesforceUser) => {
    setSelectedUserForPermissions(user);
  };

  const handlePermissionsDialogClose = () => {
    setSelectedUserForPermissions(null);
    fetchUsers(); // Refresh the user list to show updated permissions
  };

  const saveUserPermissions = async (userId: string, permissions: Permission[]) => {
    try {
      await axios.put(`/api/users/${userId}/permissions`, { permissions });
      setSuccess('Permissions updated successfully!');
      fetchUsers(); // Refresh user list
    } catch (err) {
      console.error('Error updating permissions:', err);
      setError('Failed to update permissions.');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get<SalesforceUser[]>('/api/users');
      setUsers(response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to fetch users.');
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleOpen = (user?: SalesforceUser) => {
    setError(null);
    setSuccess(null);
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.Email__c,
        first_name: user.First_Name__c,
        last_name: user.Last_Name__c,
        password: '',
        role: user.Role__c,
        is_active: user.Is_Active__c,
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        first_name: '',
        last_name: '',
        password: '',
        role: 'user',
        is_active: true,
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingUser(null);
  };

  const handlePasswordDialogOpen = (user: SalesforceUser) => {
    setEditingUser(user);
    setPassword('');
    setError(null);
    setSuccess(null);
    setOpenPasswordDialog(true);
  };

  const handlePasswordDialogClose = () => {
    setOpenPasswordDialog(false);
    setEditingUser(null);
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser || !password) {
      setError('Password cannot be empty.');
      return;
    }
    try {
      await axios.post(`/api/users/${editingUser.Id}/reset-password`, { password });
      setSuccess(`Password for ${editingUser.Email__c} has been reset successfully.`);
      handlePasswordDialogClose();
    } catch (err) {
      console.error('Error resetting password:', err);
      setError('Failed to reset password.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        first_name: formData.first_name, // ensure snake_case for backend
        last_name: formData.last_name,
        is_active: formData.is_active,
      };

      if (editingUser) {
        await axios.put(`/api/users/${editingUser.Id}`, payload);
        setSuccess('User updated successfully.');
      } else {
        await axios.post('/api/users', payload);
        setSuccess('User created successfully.');
      }
      handleClose();
      fetchUsers();
    } catch (err) {
      console.error('Error saving user:', err);
      setError('Failed to save user.');
    }
  };
  
  const handleDelete = async (userId: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await axios.delete(`/api/users/${userId}`);
        setSuccess('User deleted successfully.');
        fetchUsers();
      } catch (err) {
        console.error('Error deleting user:', err);
        setError('Failed to delete user.');
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };
  
  const handleRoleChange = (e: SelectChangeEvent<string>) => {
      setFormData(prev => ({
          ...prev,
          role: e.target.value
      }));
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">User Management</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" color="primary" onClick={() => handleOpen()}>
            Add User
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, mt: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, mt: 2 }}>{success}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.Id}>
                <TableCell>{`${user.First_Name__c} ${user.Last_Name__c}`}</TableCell>
                <TableCell>{user.Email__c}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip 
                      label={user.Role__c} 
                      color={user.Role__c === 'admin' ? 'error' : 'default'}
                      size="small"
                    />
                                         <Tooltip title={
                       <Box sx={{ p: 1 }}>
                         <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                           {user.Permissions__c ? 'Custom Permissions:' : `${user.Role__c.charAt(0).toUpperCase() + user.Role__c.slice(1)} Permissions:`}
                         </Typography>
                         {getUserPermissions(user).map((permission, index) => (
                           <Typography key={index} variant="body2" sx={{ fontSize: '0.75rem' }}>
                             • {permission.replace('_', ' ')}
                           </Typography>
                         ))}
                         {user.Permissions__c && (
                           <Typography variant="caption" sx={{ mt: 1, display: 'block', fontStyle: 'italic', color: 'warning.main' }}>
                             Custom permissions override role defaults
                           </Typography>
                         )}
                       </Box>
                     }>
                      <SecurityIcon sx={{ fontSize: 16, color: 'action.active', cursor: 'pointer' }} />
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell>{user.Is_Active__c ? 'Active' : 'Inactive'}</TableCell>
                <TableCell>
                  {user.Last_Login__c
                    ? new Date(user.Last_Login__c).toLocaleString()
                    : 'Never'}
                </TableCell>
                <TableCell>
                  <Tooltip title="Edit User Details">
                    <IconButton onClick={() => handleOpen(user)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Reset Password">
                    <IconButton onClick={() => handlePasswordDialogOpen(user)}>
                      <PasswordIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Manage Permissions">
                    <IconButton onClick={() => handlePermissionsDialogOpen(user)}>
                      <SecurityIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete User">
                    <IconButton onClick={() => handleDelete(user.Id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit/Add User Dialog */}
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="sm" 
        fullWidth
        BackdropProps={universalModalBackdropProps}
        PaperProps={universalModalPaperProps}
      >
        <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField margin="dense" name="first_name" label="First Name" type="text" fullWidth required value={formData.first_name} onChange={handleChange} />
            <TextField margin="dense" name="last_name" label="Last Name" type="text" fullWidth required value={formData.last_name} onChange={handleChange} />
            <TextField margin="dense" name="email" label="Email Address" type="email" fullWidth required value={formData.email} onChange={handleChange} />
            <TextField margin="dense" name="password" label="Password" type="password" fullWidth required={!editingUser} helperText={editingUser ? "Leave blank to keep current password" : ""} value={formData.password} onChange={handleChange} />
            <FormControl fullWidth margin="dense" required>
              <InputLabel id="role-select-label">Role</InputLabel>
              <Select
                labelId="role-select-label"
                id="role-select"
                name="role"
                value={formData.role}
                label="Role"
                onChange={handleRoleChange}
              >
                {availableRoles.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    <Box>
                      <Typography variant="body1">{role.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {role.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {/* Show permissions for selected role */}
            {formData.role && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SecurityIcon sx={{ fontSize: 16 }} />
                  Permissions for {availableRoles.find(r => r.value === formData.role)?.label}:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {getRolePermissions(formData.role).map((permission) => (
                    <Chip
                      key={permission}
                      label={permission.replace('_', ' ')}
                      size="small"
                      variant="outlined"
                      color="primary"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  ))}
                </Box>
              </Box>
            )}
            <FormControlLabel control={<Switch checked={formData.is_active} onChange={handleChange} name="is_active" />} label="Active" />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit">{editingUser ? 'Update' : 'Create'}</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog 
        open={openPasswordDialog} 
        onClose={handlePasswordDialogClose} 
        maxWidth="sm" 
        fullWidth
        BackdropProps={universalModalBackdropProps}
        PaperProps={universalModalPaperProps}
      >
        <DialogTitle>Reset Password for {editingUser?.Email__c}</DialogTitle>
        <form onSubmit={handlePasswordSubmit}>
          <DialogContent>
            <TextField autoFocus margin="dense" name="password" label="New Password" type="password" fullWidth required value={password} onChange={(e) => setPassword(e.target.value)} />
          </DialogContent>
          <DialogActions>
            <Button onClick={handlePasswordDialogClose}>Cancel</Button>
            <Button type="submit">Set Password</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Permissions Management Dialog */}
      {selectedUserForPermissions && (
        <PermissionsManager
          selectedUser={selectedUserForPermissions}
          initialPermissions={getUserPermissions(selectedUserForPermissions)}
          onClose={handlePermissionsDialogClose}
          onSave={(permissions) => saveUserPermissions(selectedUserForPermissions.Id, permissions)}
        />
      )}
    </Container>
  );
};

export default UserManagement; 
